import { useEffect, useMemo, useState } from "react";
import { Hourglass, ShieldAlert, AlertTriangle, CheckCircle2, ArrowDown01, ArrowUp01, Search, Bell, FileDown, FileText } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { hasPerm } from "../lib/permissions";
import DateFilter from "./layout/DateFilter";

const ACCENT = "#EC4899";
const SLA_HOURS = 72;

const BAND_COLORS = {
  breached: { bar: "#F87171", chip: "border-red-500/40 bg-red-950/40 text-red-300",       label: "Breached" },
  at_risk:  { bar: "#FBBF24", chip: "border-amber-500/40 bg-amber-950/40 text-amber-300", label: "At Risk"  },
  fresh:    { bar: "#34D399", chip: "border-emerald-500/40 bg-emerald-950/40 text-emerald-300", label: "Fresh" },
};

function fmtDays(h) {
  if (h === null || h === undefined || isNaN(h)) return "—";
  const days = h / 24;
  if (days < 1) {
    if (h < 1) return `${Math.round(h * 60)}m`;
    return `${h.toFixed(1)}h`;
  }
  return `${days.toFixed(1)}d`;
}
function fmtRemaining(h) {
  if (h === null || h === undefined || isNaN(h)) return "—";
  if (h >= 0) {
    if (h < 1) return `${Math.round(h * 60)}m left`;
    if (h < 48) return `${h.toFixed(1)}h left`;
    return `${(h / 24).toFixed(1)}d left`;
  }
  const over = Math.abs(h);
  if (over < 48) return `${over.toFixed(1)}h over`;
  return `${(over / 24).toFixed(1)}d over`;
}

export default function MoldNcrOpenCard({ showMexico = false, dept = 'MOLD' }) {
  const { user } = useAuth();
  const canSubscribe = hasPerm(user, "ncr_alerts", "subscribe");
  const [data, setData] = useState({ rows: [], total: 0, breached: 0, at_risk: 0, fresh: 0, oldest_age: 0 });
  const [loading, setLoading] = useState(true);
  const [bandFilter, setBandFilter] = useState("all");   // all | breached | at_risk | fresh
  const [sortDir, setSortDir] = useState("desc");        // desc=oldest first
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({ preset: "all" });
  const [subState, setSubState] = useState({ loaded: false, subscribed: false });

  const dateParams = useMemo(() => {
    const p = { preset: filter.preset };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/departments/${dept}/ncr-open`, {
          params: { show_mexico: showMexico, ...dateParams },
        });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [showMexico, dept, dateParams]);

  const handleExport = (fmt) => {
    const qs = new URLSearchParams({
      fmt, show_mexico: String(showMexico), preset: filter.preset,
    });
    if (filter.preset === "custom") { qs.set("start", filter.start); qs.set("end", filter.end); }
    if (bandFilter && bandFilter !== "all") qs.set("band", bandFilter);
    if (search && search.trim()) qs.set("search", search.trim());
    const token = localStorage.getItem("token");
    if (token) qs.set("token", token);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/departments/${dept}/ncr-open/export?${qs.toString()}`;
    window.location.href = url;
  };

  // Load subscription status for the bell button
  useEffect(() => {
    if (!canSubscribe) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/ncr-alerts/my-subscription");
        if (cancelled) return;
        const s = r.data?.subscription;
        setSubState({
          loaded: true,
          subscribed: !!(s && s.active && (s.depts || []).includes(dept)),
        });
      } catch {
        if (!cancelled) setSubState({ loaded: true, subscribed: false });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [canSubscribe, dept]);

  const handleQuickSubscribe = async () => {
    try {
      // Fetch current sub to merge depts so we don't drop the other one
      const current = await api.get("/ncr-alerts/my-subscription").then((r) => r.data?.subscription).catch(() => null);
      const existingDepts = current?.depts || [];
      const willSubscribe = !subState.subscribed;
      const nextDepts = willSubscribe
        ? Array.from(new Set([...existingDepts, dept]))
        : existingDepts.filter((d) => d !== dept);
      if (!willSubscribe && nextDepts.length === 0) {
        await api.delete("/ncr-alerts/my-subscription");
        setSubState({ loaded: true, subscribed: false });
        toast.success(`Unsubscribed from ${dept} alerts`);
        return;
      }
      await api.put("/ncr-alerts/my-subscription", {
        email: current?.email || user?.email || "",
        depts: nextDepts,
        daily_digest: current?.daily_digest ?? true,
        instant_alerts: current?.instant_alerts ?? true,
        active: true,
      });
      setSubState({ loaded: true, subscribed: willSubscribe });
      toast.success(willSubscribe ? `Subscribed to ${dept} SLA alerts` : `Unsubscribed from ${dept} alerts`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rows = useMemo(() => {
    let r = data.rows || [];
    if (bandFilter !== "all") r = r.filter((x) => x.band === bandFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) =>
        (x.job || "").toLowerCase().includes(q) ||
        (x.piece_mark || "").toLowerCase().includes(q) ||
        (x.piece_sn || "").toLowerCase().includes(q) ||
        (x.problem || "").toLowerCase().includes(q) ||
        (x.cause || "").toLowerCase().includes(q)
      );
    }
    return [...r].sort((a, b) =>
      sortDir === "desc" ? b.age_hours - a.age_hours : a.age_hours - b.age_hours
    );
  }, [data.rows, bandFilter, sortDir, search]);

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden p-5"
      data-testid="mold-ncr-open-card"
    >
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Hourglass size={18} style={{ color: ACCENT }} />
          <h3 className="font-heading font-bold text-xl">Open NCRs · SLA Watch</h3>
          {canSubscribe && (
            <button
              data-testid="ncr-open-subscribe-btn"
              type="button"
              onClick={handleQuickSubscribe}
              title={subState.subscribed ? `Unsubscribe from ${dept} SLA alerts` : `Subscribe to ${dept} SLA alerts`}
              className={`ml-2 inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] tracking-widest uppercase transition-colors ${
                subState.subscribed
                  ? "bg-[#EC4899]/10 border-[#EC4899]/60 text-[#EC4899]"
                  : "bg-[#0A0B10] border-border text-muted-foreground hover:text-white hover:border-[#EC4899]/60"
              }`}
            >
              <Bell size={11} fill={subState.subscribed ? "#EC4899" : "none"} />
              {subState.subscribed ? "Subscribed" : "Alert me"}
            </button>
          )}
        </div>
        <span className="text-[10px] tracking-widest uppercase text-muted-foreground font-mono">
          Plant: {data.plant || (dept === "FRAME"
            ? (showMexico ? "FRAME-MEX" : "FRAME-SLC")
            : dept === "GFRC"
            ? (showMexico ? "GFRG-MEXICO / MXUD" : "GFRG-SLC / GFRC-MOLDS")
            : dept === "FINISH"
            ? (showMexico ? "FINISH · MEXICO" : "FINISH · SLC")
            : dept === "FIBERGLASS"
            ? (showMexico ? "FRGMEX / GFRG-MEX" : "FRGSLC")
            : (showMexico ? "MOLDS-MEX" : "MOLDS-SLC"))} · SLA: {SLA_HOURS}h
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        NCRs still open. Anything past 72h since creation is flagged for the team lead.
      </p>

      {/* Date filter */}
      <div className="mb-4">
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border mb-5" data-testid="ncr-open-stats">
        <Tile
          label="Total Open"  value={data.total}     icon={<Hourglass size={12} />}
          accent={ACCENT} testId="open-tile-total"
        />
        <Tile
          label="Breached" value={data.breached} icon={<ShieldAlert size={12} />}
          accent="#F87171" sub={data.total ? `${Math.round((data.breached / data.total) * 100)}%` : "—"}
          testId="open-tile-breached"
        />
        <Tile
          label="At Risk" value={data.at_risk} icon={<AlertTriangle size={12} />}
          accent="#FBBF24" sub="48–72h" testId="open-tile-atrisk"
        />
        <Tile
          label="Fresh" value={data.fresh} icon={<CheckCircle2 size={12} />}
          accent="#34D399" sub="< 48h" testId="open-tile-fresh"
        />
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="ncr-open-filters">
        <div className="inline-flex border border-border">
          {[
            ["all",      `All (${data.total})`],
            ["breached", `Breached (${data.breached})`],
            ["at_risk",  `At Risk (${data.at_risk})`],
            ["fresh",    `Fresh (${data.fresh})`],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              data-testid={`band-chip-${k}`}
              onClick={() => setBandFilter(k)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${
                bandFilter === k
                  ? "bg-[#EC4899] text-[#0A0B10]"
                  : "bg-[#0A0B10] text-muted-foreground hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="ncr-open-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job / piece / problem / cause…"
            className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#EC4899]/60"
          />
        </div>

        <button
          type="button"
          data-testid="ncr-open-sort"
          onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
          className="flex items-center gap-1.5 bg-[#0A0B10] border border-border px-3 py-1.5 text-xs uppercase tracking-widest hover:border-[#EC4899]/60 transition-colors"
          title="Toggle days sort"
        >
          {sortDir === "desc" ? <ArrowDown01 size={12} /> : <ArrowUp01 size={12} />}
          Days {sortDir === "desc" ? "↓" : "↑"}
        </button>

        <button
          type="button"
          data-testid="ncr-open-export-csv"
          onClick={() => handleExport("csv")}
          title="Download filtered rows as CSV"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-border bg-[#0A0B10] text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white hover:border-[#EC4899]/60 transition-colors"
        >
          <FileDown size={11} /> CSV
        </button>
        <button
          type="button"
          data-testid="ncr-open-export-pdf"
          onClick={() => handleExport("pdf")}
          title="Download filtered rows as PDF"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-border bg-[#0A0B10] text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white hover:border-[#EC4899]/60 transition-colors"
        >
          <FileText size={11} /> PDF
        </button>
      </div>

      {/* Table */}
      {loading && (
        <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground">
          Loading…
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="py-12 text-center text-sm uppercase tracking-widest text-muted-foreground"
             data-testid="ncr-open-empty">
          {data.total === 0 ? "No open NCRs — nice work" : "No NCRs match the filter"}
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto" data-testid="ncr-open-table">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8]">
              <tr>
                <th className="py-2.5 px-3 w-[90px]">Band</th>
                <th className="py-2.5 px-3 w-[100px]">Opened</th>
                <th className="py-2.5 px-3 w-[90px] text-right">Days</th>
                <th className="py-2.5 px-3">Job</th>
                <th className="py-2.5 px-3">Piece</th>
                <th className="py-2.5 px-3">Problem</th>
                <th className="py-2.5 px-3 w-[110px]">Cause</th>
                <th className="py-2.5 px-3 w-[120px] text-right">SLA</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const b = BAND_COLORS[r.band];
                const pct = Math.min(100, (r.age_hours / SLA_HOURS) * 100);
                return (
                  <tr
                    key={`${r.job}-${r.piece_sn}-${i}`}
                    className="border-t border-border/40 hover:bg-[#1A1C28]/40 align-top"
                    data-testid={`ncr-open-row-${i}`}
                  >
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[9px] tracking-widest uppercase border font-mono ${b.chip}`}>
                        {b.label}
                      </span>
                    </td>
                    <td className="py-3 px-3 font-mono text-muted-foreground">{r.date}</td>
                    <td className="py-3 px-3 font-mono text-right tabular-nums text-white">
                      {fmtDays(r.age_hours)}
                    </td>
                    <td className="py-3 px-3 max-w-[200px] truncate" title={r.job}>{r.job}</td>
                    <td className="py-3 px-3 font-mono">
                      <div>{r.piece_mark || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">{r.piece_sn || ""}</div>
                    </td>
                    <td className="py-3 px-3" title={r.problem}>
                      <div className="truncate max-w-[260px]">{r.problem || "—"}</div>
                      <div className="text-[10px] text-muted-foreground truncate max-w-[260px]">{r.item || ""}</div>
                    </td>
                    <td className="py-3 px-3 text-muted-foreground">{r.cause || "—"}</td>
                    <td className="py-3 px-3 text-right">
                      <div className="font-mono text-[11px]" style={{ color: b.bar }}>
                        {fmtRemaining(r.sla_remaining_hours)}
                      </div>
                      <div className="relative h-1.5 bg-[#1F2230] mt-1 overflow-hidden">
                        <div
                          className="absolute top-0 left-0 h-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: b.bar }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Tile({ label, value, icon, accent, sub, testId }) {
  return (
    <div data-testid={testId} className="bg-[#12141D] p-4 flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
        <span style={{ color: accent }}>{icon}</span>
        {label}
      </div>
      <div className="font-heading font-bold text-3xl tabular-nums" style={{ color: accent }}>
        {value ?? 0}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
